<script lang="ts" setup>
import { useBrowserLocation } from '@vueuse/core'
import { speedbumpText } from '@consumer/constants/disclaimers'
import filesIcon from '@consumer/assets/icons/new/files.svg?svgicon'
import shieldIcon from '@consumer/assets/icons/new/shield.svg?svgicon'
import giftClearIcon from '@consumer/assets/icons/new/gift-clear.svg?svgicon'
import penIcon from '@consumer/assets/icons/new/pen.svg?svgicon'
import envelopeIcon from '@consumer/assets/icons/new/envelope.svg?svgicon'
import creditCardIcon from '@consumer/assets/icons/new/credit-card.svg?svgicon'

const location = useBrowserLocation()

const isActive = (link: { url: string }) => {
  const currentPath = location.value.pathname
  const currentHash = location.value.hash

  if (link.url === '/terms' && currentPath === '/legal') {
    return true
  }

  const [path, hash] = link.url.split('#')

  if (currentHash) {
    return currentPath === path && currentHash === `#${hash}`
  }
  else {
    return currentPath === path && !hash
  }
}

const LINKS = [
  {
    label: 'Terms of Service',
    url: '/terms',
    icon: filesIcon,
  },
  {
    label: 'Privacy Policy',
    url: '/privacy',
    icon: shieldIcon,
  },
  {
    label: 'Gift Agreement',
    url: '/giftagreement',
    icon: giftClearIcon,
  },
  {
    label: 'E-Sign Disclosure and Consent',
    url: '/giftagreement#esign',
    icon: penIcon,
  },
  {
    label: 'Gift Privacy Policy',
    url: '/giftagreement#facts',
    icon: envelopeIcon,
  },
  {
    label: 'Column N.A. Gift Card Agreement',
    url: '/cardholderagreement',
    icon: creditCardIcon,
  },
  {
    label: 'Column N.A. Privacy Policy',
    url: '/columnprivacy',
    icon: shieldIcon,
    speedbump: true,
  },
]

onMounted(() => {
  window.__SPEEDBUMP_TEXT__ = speedbumpText
})

declare global {
  interface Window {
    __SPEEDBUMP_TEXT__: string
  }
}
</script>

<template>
  <div class="bg-white paragraphs legal-container">
    <GContainer class="py-20">
      <h1 class="legal-page-title">Giftly Legal Notices</h1>
      <div class="mt-8 lg:mt-16 flex flex-col lg:flex-row justify-between gap-7 lg:gap-14">
        <div class="legal-sidebar flex-nowrap whitespace-nowrap space-y-4">
          <InertiaLink
            v-for="link, i in LINKS"
            :key="i"
            class="
              border-1
              p-4
              block
              flex gap-2 items-center
              font-semibold
              rounded-v2sm
              hover:no-underline
              hover:bg-primary-pale
            "
            :class="[
              isActive(link) ?
                'border-primary-base bg-primary-pale text-navy' :
                'border-grey-400 bg-white text-grey-700',
            ]"
            :href="link.url"
            :onclick="link.speedbump ? `return confirm(__SPEEDBUMP_TEXT__)` : ''"
          >
            <GIcon
              :icon="link.icon"
              size="24px"
              :color="isActive(link) ?'primary-base' : 'grey-700'"
            />
            {{ link.label }}
          </InertiaLink>
        </div>
        <div class="flex-1">
          <slot/>
        </div>
      </div>
    </GContainer>
  </div>
</template>

<style scoped>
@media print {
  .legal-container {
    padding: 0 !important;
  }

  .legal-page-title {
    display: none;
  }

  .legal-sidebar {
    display: none;
  }
}
</style>

<style>
@media print {
  html,
  body,
  #app,
  main,
  [data-qa="PageContent"] {
    height: auto !important;
  }

  .legal-container .container {
    max-width: 100% !important;
    padding: 0 !important;
  }

  .legal-container a[onclick*="window.print"] {
    display: none !important;
  }

  .legal-container h1 {
    font-size: 20px !important;
  }

  .legal-container h2 {
    font-size: 14px !important;
    margin-bottom: 0.25rem !important;
    margin-top: 1rem !important;
  }

  .legal-container h3 {
    font-size: 13px !important;
    margin-bottom: 0.25rem !important;
    margin-top: 0.75rem !important;
  }

  .legal-container h4 {
    font-size: 12px !important;
    margin-bottom: 0.25rem !important;
  }

  .legal-container p,
  .legal-container li {
    font-size: 11px !important;
    line-height: 1.5 !important;
  }
}
</style>
